import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import { ToastContainer } from "react-toastify";

import { BrandsApi } from "apis/BrandsApi";
import { SchemesV3Api, TargetGroupTemplatesV3Api } from "apis/SchemesV3Api";
import {
  SchemeRow,
  SchemeFormModal,
  TemplateFormModal,
  EarningsTestPanel,
  SCHEME_STATUS_OPTIONS,
} from "components/SchemesV3";
import CircularIndeterminateModal from "../../utils/Loading";
import { getDefaultDates, showToast } from "utils";
import {
  ActionButton,
  SearchInput,
  Toolbar as UIToolbar,
  TableCard,
  FormCard,
  DataTable,
  EmptyRow,
} from "utils/ui";

const ALL_BRANDS = "__ALL__";

const handleApiError = (err, navigate) => {
  if (err?.response?.data) showToast(`${err.response.status} ${err.response.data.message}`);
  if (err?.response?.status === 401) {
    localStorage.clear();
    navigate("/");
  }
};

export default function SchemesV3() {
  const navigate = useNavigate();
  const defaultDates = getDefaultDates();

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);

  const [brands, setBrands] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState("");

  /* ── Schemes tab ── */
  const [schemes, setSchemes] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [keyword, setKeyword] = useState("");
  const [datePayload, setDatePayload] = useState(defaultDates);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [schemeModalOpen, setSchemeModalOpen] = useState(false);
  const [editingScheme, setEditingScheme] = useState(null);

  /* ── Templates tab ── */
  const [templates, setTemplates] = useState([]);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  /* ── Earnings tab ── */
  const [activeSchemes, setActiveSchemes] = useState([]);

  useEffect(() => {
    fetchBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedBrandId) return;
    if (tab === 0) fetchSchemes(0);
    if (tab === 1) fetchTemplates();
    if (tab === 2) fetchActiveSchemes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrandId, tab, statusFilter, datePayload]);

  const fetchBrands = () => {
    setLoading(true);
    BrandsApi.GetBrandsListByFilter({ page: 0, size: 100 })
      .then((res) => {
        const activeBrands = (res.data.data || []).filter((d) => d.isActive);
        setBrands(activeBrands);
        if (activeBrands.length > 0) setSelectedBrandId(activeBrands[0].id);
      })
      .catch((err) => handleApiError(err, navigate))
      .finally(() => setLoading(false));
  };

  const fetchSchemes = (page) => {
    if (!selectedBrandId) return;
    setLoading(true);

    if (selectedBrandId === ALL_BRANDS) {
      // /v3/schemes/filter requires a brandId, so "All Brands" fans the same
      // filter out across every brand and merges client-side. Real
      // server-side pagination doesn't apply here — each brand is capped at
      // 200 schemes, which is well beyond what any brand has today.
      Promise.all(
        brands.map((b) =>
          SchemesV3Api.GetByFilter({
            brandId: b.id,
            periodFrom: datePayload.periodFrom,
            periodTo: datePayload.periodTo,
            status: statusFilter || undefined,
            keyword: keyword || undefined,
            page: 0,
            size: 200,
          }).catch(() => null)
        )
      )
        .then((results) => {
          const merged = results
            .filter(Boolean)
            .flatMap((res) => res.data?.data?.data || []);
          setSchemes(merged);
          setTotalElements(merged.length);
          setTotalPages(1);
        })
        .catch((err) => handleApiError(err, navigate))
        .finally(() => setLoading(false));
      return;
    }

    SchemesV3Api.GetByFilter({
      brandId: selectedBrandId,
      periodFrom: datePayload.periodFrom,
      periodTo: datePayload.periodTo,
      status: statusFilter || undefined,
      keyword: keyword || undefined,
      page,
      size: 20,
    })
      .then((res) => {
        const payload = res.data?.data || {};
        setSchemes(payload.data || []);
        setTotalElements(payload.totalElements || 0);
        setTotalPages(payload.totalPages || 0);
      })
      .catch((err) => handleApiError(err, navigate))
      .finally(() => setLoading(false));
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setKeyword(value);
    clearTimeout(handleSearch._t);
    handleSearch._t = setTimeout(() => {
      setCurrentPage(1);
      fetchSchemes(0);
    }, 500);
  };

  const handlePageChange = (_, page) => {
    setCurrentPage(page);
    fetchSchemes(page - 1);
  };

  const handleReset = () => {
    setDatePayload(defaultDates);
    setStatusFilter("");
    setKeyword("");
    setCurrentPage(1);
    fetchSchemes(0);
  };

  const runLifecycleAction = (apiCall, successMessage) => {
    setLoading(true);
    apiCall
      .then(() => {
        showToast(successMessage, "success");
        fetchSchemes(currentPage - 1);
      })
      .catch((err) => showToast(err?.response?.data?.message || "Request failed"))
      .finally(() => setLoading(false));
  };

  const handleSubmitForReview = (scheme) =>
    runLifecycleAction(SchemesV3Api.Submit(scheme.id), "Scheme submitted for review (IN_REVIEW)");
  const handlePublish = (scheme) =>
    runLifecycleAction(SchemesV3Api.Publish(scheme.id), "Scheme published (ACTIVE)");
  const handleArchive = (scheme) => {
    if (!window.confirm(`Archive "${scheme.name}"? This hides it from active lists (no delete, fully reversible by data, not by UI).`)) return;
    runLifecycleAction(SchemesV3Api.Archive(scheme.id), "Scheme archived");
  };

  /* ── Templates ── */
  const fetchTemplates = () => {
    if (!selectedBrandId) return;
    setLoading(true);

    if (selectedBrandId === ALL_BRANDS) {
      Promise.all(
        brands.map((b) =>
          TargetGroupTemplatesV3Api.GetByFilter(b.id)
            .then((res) => (res.data?.data || []).map((t) => ({ ...t, brandId: t.brandId || b.id, _brandName: b.name })))
            .catch(() => [])
        )
      )
        .then((results) => setTemplates(results.flat()))
        .catch((err) => handleApiError(err, navigate))
        .finally(() => setLoading(false));
      return;
    }

    TargetGroupTemplatesV3Api.GetByFilter(selectedBrandId)
      .then((res) => setTemplates(res.data?.data || []))
      .catch((err) => handleApiError(err, navigate))
      .finally(() => setLoading(false));
  };

  const handleArchiveTemplate = (template) => {
    if (!window.confirm(`Archive template "${template.name}"? Schemes that already copied it are unaffected.`)) return;
    setLoading(true);
    TargetGroupTemplatesV3Api.Archive(template.id)
      .then(() => {
        showToast("Template archived", "success");
        fetchTemplates();
      })
      .catch((err) => showToast(err?.response?.data?.message || "Error archiving template"))
      .finally(() => setLoading(false));
  };

  /* ── Earnings ── */
  const fetchActiveSchemes = () => {
    if (!selectedBrandId) return;
    setLoading(true);

    if (selectedBrandId === ALL_BRANDS) {
      Promise.all(
        brands.map((b) =>
          SchemesV3Api.GetByFilter({
            brandId: b.id,
            periodFrom: "2000-01-01",
            periodTo: "2100-01-01",
            status: "ACTIVE",
            page: 0,
            size: 100,
          }).catch(() => null)
        )
      )
        .then((results) => {
          const merged = results.filter(Boolean).flatMap((res) => res.data?.data?.data || []);
          setActiveSchemes(merged);
        })
        .catch((err) => handleApiError(err, navigate))
        .finally(() => setLoading(false));
      return;
    }

    SchemesV3Api.GetByFilter({
      brandId: selectedBrandId,
      periodFrom: "2000-01-01",
      periodTo: "2100-01-01",
      status: "ACTIVE",
      page: 0,
      size: 100,
    })
      .then((res) => setActiveSchemes(res.data?.data?.data || []))
      .catch((err) => handleApiError(err, navigate))
      .finally(() => setLoading(false));
  };

  return (
    <>
      <CircularIndeterminateModal open={loading} />

      <FormCard>
        <Box sx={{ p: 2 }}>
          <Chip label="V3" color="primary" size="small" sx={{ mb: 1, fontWeight: 700 }} />
          <FormControl fullWidth size="small">
            <InputLabel>Brand</InputLabel>
            <Select
              label="Brand"
              value={selectedBrandId}
              onChange={(e) => { setSelectedBrandId(e.target.value); setCurrentPage(1); }}
            >
              <MenuItem value={ALL_BRANDS}><em>All Brands</em></MenuItem>
              {brands.map((b) => (
                <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderTop: "1px solid #E2E8F0" }}>
          <Tab label="Schemes" />
          <Tab label="Templates" />
          <Tab label="Earnings Test" />
        </Tabs>
      </FormCard>

      {tab === 0 && (
        <>
          <FormCard>
            <UIToolbar>
              <SearchInput placeholder="Search by scheme name" value={keyword} onChange={handleSearch} />
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
                  <MenuItem value="">All</MenuItem>
                  {SCHEME_STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                type="date"
                label="Period From"
                value={datePayload.periodFrom}
                onChange={(e) => setDatePayload((p) => ({ ...p, periodFrom: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                size="small"
                type="date"
                label="Period To"
                value={datePayload.periodTo}
                onChange={(e) => setDatePayload((p) => ({ ...p, periodTo: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
              <ActionButton variant="ghost" onClick={handleReset}>Reset</ActionButton>
              <ActionButton icon="fas fa-plus" onClick={() => { setEditingScheme(null); setSchemeModalOpen(true); }}>
                Add Scheme
              </ActionButton>
            </UIToolbar>
          </FormCard>

          <TableCard>
            <Stack direction={{ md: "row", xs: "column" }} justifyContent="space-between" alignItems="center" sx={{ p: 2 }}>
              <div style={{ color: "#0EA5E9" }}>
                Total: {totalElements}
                {selectedBrandId === ALL_BRANDS && " (across all brands, up to 200 per brand)"}
              </div>
              {selectedBrandId !== ALL_BRANDS && (
                <Pagination count={totalPages} page={currentPage} color="primary" onChange={handlePageChange} size="small" />
              )}
            </Stack>
            <DataTable headers={["#", "Name / Brand", "Period From", "Period To", "Status", "Version", "Actions"]}>
              {schemes.length === 0 && <EmptyRow message="No V3 schemes found for this brand/filter" colSpan={7} />}
              {schemes.map((scheme, i) => (
                <SchemeRow
                  key={scheme.id || i}
                  scheme={scheme}
                  index={i + (currentPage - 1) * 20}
                  onEdit={(s) => { setEditingScheme(s); setSchemeModalOpen(true); }}
                  onSubmit={handleSubmitForReview}
                  onPublish={handlePublish}
                  onArchive={handleArchive}
                />
              ))}
            </DataTable>
          </TableCard>
        </>
      )}

      {tab === 1 && (
        <>
          <FormCard>
            <UIToolbar>
              <div className="flex-1" />
              <ActionButton icon="fas fa-plus" onClick={() => { setEditingTemplate(null); setTemplateModalOpen(true); }}>
                New Template
              </ActionButton>
            </UIToolbar>
          </FormCard>
          <TableCard>
            <DataTable headers={selectedBrandId === ALL_BRANDS ? ["Name", "Brand", "Products", "Filters", "Actions"] : ["Name", "Products", "Filters", "Actions"]}>
              {templates.length === 0 && <EmptyRow message="No templates for this brand" colSpan={selectedBrandId === ALL_BRANDS ? 5 : 4} />}
              {templates.map((t) => (
                <tr key={t.id}>
                  <td style={{ padding: "0.7rem 1rem", fontSize: "0.8rem" }}>{t.name}</td>
                  {selectedBrandId === ALL_BRANDS && (
                    <td style={{ padding: "0.7rem 1rem", fontSize: "0.8rem" }}>
                      {t._brandName || brands.find((b) => b.id === t.brandId)?.name || "—"}
                    </td>
                  )}
                  <td style={{ padding: "0.7rem 1rem", fontSize: "0.8rem" }}>{(t.productIds || []).length} products</td>
                  <td style={{ padding: "0.7rem 1rem", fontSize: "0.8rem" }}>
                    {t.attributeFilters ? <Chip size="small" label="Has filters" /> : "—"}
                  </td>
                  <td style={{ padding: "0.7rem 1rem" }}>
                    <div className="flex gap-1.5">
                      <ActionButton size="small" variant="secondary" onClick={() => { setEditingTemplate(t); setTemplateModalOpen(true); }}>
                        Edit
                      </ActionButton>
                      <ActionButton size="small" variant="danger" onClick={() => handleArchiveTemplate(t)}>
                        Archive
                      </ActionButton>
                    </div>
                  </td>
                </tr>
              ))}
            </DataTable>
          </TableCard>
        </>
      )}

      {tab === 2 && (
        <TableCard>
          <Box sx={{ p: { xs: 2, md: 3 } }}>
            <EarningsTestPanel activeSchemes={activeSchemes} />
          </Box>
        </TableCard>
      )}

      <SchemeFormModal
        open={schemeModalOpen}
        onClose={() => setSchemeModalOpen(false)}
        brands={brands}
        defaultPeriod={defaultDates}
        editingScheme={editingScheme}
        onSaved={() => fetchSchemes(currentPage - 1)}
      />

      <TemplateFormModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        brands={brands}
        defaultBrandId={selectedBrandId === ALL_BRANDS ? "" : selectedBrandId}
        editingTemplate={editingTemplate}
        onSaved={fetchTemplates}
      />

      <ToastContainer />
    </>
  );
}
